import { Avatar, Empty, LifeIdDrawer, ListItem, Loader } from '@components/ui';
import ListItemTitleDescIcon from '@components/ui/list-item/components/list-item-title-desc-icon';
import useEnvService from '@hooks/app/useEnvService';
import useClientSearchList from '@hooks/query/clients/useClientSearchList';
import getFullNameFromProfile from '@lib/utils/names/getFullNameFromProfile';
import { useVirtualizer } from '@tanstack/react-virtual';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';

const PatientsMobile: React.FunctionComponent = () => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useClientSearchList();
  const theme = useTheme();
  const history = useHistory();

  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? data.length + 1 : data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 57,
    overscan: 5,
  });
  const items = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= data.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, data.length, isFetchingNextPage, items]);

  return (
    <div
      style={{
        height: 'calc(100vh - 60px)',
        background: theme.color.surface.baseline,
        overflowY: 'auto',
      }}
      ref={parentRef}
    >
      {data.length === 0 ? (
        <div className="mdl-no-records">
          <Empty style={{ margin: 32 }} title={<FormattedMessage id="med_records.no_records" />} />
        </div>
      ) : (
        <LifeIdDrawer.Scroller
          totalHeight={rowVirtualizer.getTotalSize()}
          startPoint={items[0].start}
        >
          <ListItem.Group>
            {items.map((virtualRow) => {
              const isLoaderRow = virtualRow.index > data.length - 1;
              const item = data[virtualRow.index];
              const extra = {
                key: virtualRow.key,
                'data-index': virtualRow.index,
                ref: rowVirtualizer.measureElement,
              };

              return (
                <>
                  {isLoaderRow ? (
                    hasNextPage ? (
                      <div className="load-more" {...extra}>
                        <Loader size="small" />
                      </div>
                    ) : (
                      <div className="load-more" {...extra}>
                        Načítať viac
                      </div>
                    )
                  ) : (
                    <ListItem
                      {...extra}
                      style={{ paddingLeft: 24, paddingRight: 24 }}
                      onClick={() => {
                        history.push(`/life-id/${item.id}`);
                      }}
                      left={
                        <Avatar
                          src={item.profile.profileImage}
                          size="Xsmall"
                          style={{ marginRight: 12 }}
                        />
                      }
                    >
                      <ListItemTitleDescIcon title={getFullNameFromProfile(item.profile)} />
                    </ListItem>
                  )}
                </>
              );
            })}
          </ListItem.Group>
        </LifeIdDrawer.Scroller>
      )}
    </div>
  );
};

export default React.memo(PatientsMobile);
